import {useEffect, useRef, useState} from 'react';

import * as api from '../api';
import {ApiError} from '../api';
import {useStudio} from '../store';
import type {Scene, SceneView} from '../types';

import {Pause, Play} from './Icons';

/* A fixed bar pattern, computed once at module load so the waveform does not
   twitch on every re-render. Percentages of the track height; the design's
   silhouette, not the real audio — we never decode the file, and drawing
   invented peaks as if they were measured would be fabricated data.
   The count is high because these bars stretch to fill the block: at ~30 bars
   across a 1000px column each one is 30px wide and the row reads as a string of
   pills rather than a waveform. */
const BARS = Array.from({length: 120}, (_, i) =>
  Math.round(
    22 +
      78 *
        Math.abs(
          Math.sin(i * 0.37) * 0.55 + Math.sin(i * 0.11) * 0.3 + Math.sin(i * 0.83) * 0.15,
        ),
  ),
);

/**
 * The bars are DOM elements rather than one SVG because each one has to animate
 * on its own delay while playing (`lg-wave-play`) — a clip-path over a static
 * <svg> can show a play *head* but cannot make the waveform move.
 */
function Wave({progress, live, count = BARS.length}: {progress: number; live: boolean; count?: number}) {
  const bars = count === BARS.length ? BARS : BARS.slice(0, count);
  const filled = Math.round((progress / 100) * bars.length);
  return (
    <>
      {bars.map((h, i) => (
        <i
          key={i}
          className={i < filled ? 'on' : undefined}
          style={{height: `${h}%`, animationDelay: live ? `${i * 22}ms` : undefined}}
        />
      ))}
    </>
  );
}

function clock(s: number) {
  if (!Number.isFinite(s) || s < 0) return '00:00';
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

/**
 * The scene's voice-over: a real audio player, not a decorative one.
 *
 * The audio URL is resolved lazily on first play — local voice-over files are
 * root-relative and usable straight away, but an object stored in S3 needs a presigned
 * URL, and there is no reason to mint one for every scene on screen.
 */
export function VoiceoverBlock({scene, view}: {scene: Scene; view: SceneView}) {
  const s = useStudio();
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');

  // Stop and release audio when the scene's voice-over is replaced or the card unmounts.
  useEffect(() => {
    const el = audio.current;
    return () => {
      el?.pause();
    };
  }, [scene.voiceover?.s3_key]);

  async function toggle() {
    const el = audio.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    setError('');
    try {
      if (!el.src) {
        const key = scene.voiceover?.s3_key ?? '';
        el.src = key.startsWith('media/')
          ? `/${key}`
          : (await api.voiceoverPreview(s.user!.id, s.project!.id, scene.id)).audio_url;
      }
      await el.play();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر تشغيل ملف الصوت');
    }
  }

  const header = (
    <div className="blk-head" style={{marginBottom: 10}}>
      <span className="field-lbl">التعليق الصوتي</span>
      <button
        type="button"
        className="btn-txt"
        disabled={view.voiceover === 'busy'}
        onClick={() => void s.openVoiceLibrary(scene.id)}
        title="اختر صوتًا من المكتبة"
      >
        🎙️ اختر الصوت
      </button>
    </div>
  );

  if (view.voiceover === 'busy') {
    return (
      <div className="blk">
        {header}
        {/* The generating state animates the same waveform rather than showing a
            spinner: it says "audio is being made" and, unlike a bar, it cannot be
            misread as progress — the TTS call reports none. */}
        <div className="pend">
          <div className="wave gen" dir="ltr" aria-hidden="true">
            {/* Fewer bars: this track is ~110px, and 120 of them at a 1px floor
                would overflow it. */}
            <Wave progress={0} live count={40} />
          </div>
          <span>جاري توليد التعليق الصوتي…</span>
        </div>
      </div>
    );
  }

  if (view.voiceover === 'fail') {
    return (
      <div className="blk">
        {header}
        <div className="dashed bad">
          <span className="err-note">{view.voiceoverError}</span>
          <button type="button" className="btn sm" onClick={() => void s.regenerateVoiceover(scene.id)}>
            أعد المحاولة
          </button>
        </div>
      </div>
    );
  }

  if (view.voiceover !== 'ready' || !scene.voiceover) {
    return (
      <div className="blk">
        {header}
        <div className="dashed">
          <span style={{font: '500 13.5px/1.8 var(--sans)', color: 'var(--muted)'}}>
            {scene.vo_text
              ? 'لا يوجد تعليق صوتي لهذا المشهد بعد'
              : 'لا يوجد نص صوتي لهذا المشهد — حرّر النص أولًا'}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={!scene.vo_text}
            onClick={() => void s.regenerateVoiceover(scene.id)}
          >
            ولّد التعليق الصوتي
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blk">
      {header}
      {/* LTR: the transport reads left-to-right even in an RTL page, like every player. */}
      <div className="vo" dir="ltr">
        <button type="button" className="vo-play" onClick={() => void toggle()} aria-label="تشغيل">
          {playing ? <Pause /> : <Play />}
        </button>
        <div
          className={`wave${playing ? ' live' : ''}`}
          onClick={(e) => {
            const el = audio.current;
            if (!el || !Number.isFinite(el.duration)) return;
            const box = e.currentTarget.getBoundingClientRect();
            el.currentTime = ((e.clientX - box.left) / box.width) * el.duration;
          }}
        >
          <Wave progress={progress} live={playing} />
        </div>
        <div className="vo-time">
          <b>
            {clock(elapsed)} / {clock(scene.voiceover.duration_s ?? 0)}
          </b>
          <span>صوت: {scene.voiceover.voice || '—'}</span>
        </div>
        <audio
          ref={audio}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setProgress(0);
            setElapsed(0);
          }}
          onTimeUpdate={(e) => {
            const el = e.currentTarget;
            setElapsed(el.currentTime);
            setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
          }}
          onError={() => setError('تعذّر تحميل ملف الصوت')}
        />
      </div>
      {error && (
        <div className="err-note" style={{marginTop: 8}}>
          {error}
        </div>
      )}
      {scene.vo_text && <div className="vo-quote">«{scene.vo_text}»</div>}
    </div>
  );
}
